/**
 * PayFast Payment Component
 * 
 * Handles PayFast payment initiation and form submission
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { paymentAPI } from '../../services/api';
import './PayFastPayment.css';

const PayFastPayment: React.FC = () => {
  const { paymentId } = useParams<{ paymentId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentParams, setPaymentParams] = useState<any>(null);
  const [paymentUrl, setPaymentUrl] = useState<string>('');

  useEffect(() => {
    if (!paymentId) {
      setError('Payment ID is required');
      setLoading(false);
      return;
    }

    const initiatePayment = async () => {
      try {
        setLoading(true);
        const response = await paymentAPI.initiatePayFastPayment(paymentId);
        
        if (response.success && response.data) {
          setPaymentParams(response.data.paymentParams);
          setPaymentUrl(response.data.paymentUrl);
          
          // Log payment params for debugging
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ PayFast Payment Initiated:');
            console.log('  Payment URL:', response.data.paymentUrl);
            console.log('  Payment Params:', response.data.paymentParams);
            console.log('  Signature:', response.data.paymentParams?.signature);
          }
          
          // Auto-submit form to PayFast after a short delay
          setTimeout(() => {
            const form = document.getElementById('payfast-form') as HTMLFormElement;
            if (form) {
              console.log('🚀 Submitting form to PayFast...');
              form.submit();
            } else {
              console.error('❌ Form not found!');
              setError('Payment form not found. Please try again.');
            }
          }, 1000);
        } else {
          setError(response.message || 'Failed to initiate payment');
        }
      } catch (err: any) {
        console.error('Error initiating payment:', err);
        setError(err.response?.data?.message || 'An error occurred while initiating payment');
      } finally {
        setLoading(false);
      }
    };

    initiatePayment();
  }, [paymentId]);

  if (loading) {
    return (
      <div className="payfast-payment">
        <div className="payment-loading">
          <div className="loading-spinner"></div>
          <p>Preparing your payment...</p>
          <p className="loading-hint">You will be redirected to PayFast shortly</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payfast-payment">
        <div className="payment-error">
          <div className="error-icon">❌</div>
          <h2>Payment Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payfast-payment">
      <div className="payment-container">
        <div className="payment-header">
          <h2>Redirecting to PayFast</h2>
          <p>Please wait while we redirect you to complete your payment...</p>
        </div>

        {/* Hidden form that auto-submits to PayFast */}
        {paymentParams && paymentUrl && (
          <form
            id="payfast-form"
            method="POST"
            action={paymentUrl}
            style={{ display: 'none' }}
            onError={(e) => {
              console.error('Form submission error:', e);
              setError('Failed to submit payment form to PayFast');
            }}
          >
            {Object.entries(paymentParams)
              .filter(([key, value]) => 
                !key.startsWith('custom_int') && 
                value !== null && 
                value !== undefined && 
                String(value).trim() !== ''
              ) // Filter out custom_int fields and empty values
              .map(([key, value]) => {
                // Log form fields for debugging
                if (process.env.NODE_ENV === 'development') {
                  console.log(`📝 Form field: ${key} = ${String(value).substring(0, 50)}`);
                }
                return (
                  <input
                    key={key}
                    type="hidden"
                    name={key}
                    value={String(value)}
                  />
                );
              })}
          </form>
        )}

        <div className="payment-info">
          <p>If you are not redirected automatically, click the button below:</p>
          {paymentParams && paymentUrl && (
            <form method="POST" action={paymentUrl}>
              {Object.entries(paymentParams)
                .filter(([key, value]) => 
                  !key.startsWith('custom_int') && 
                  value !== null && 
                  value !== undefined && 
                  String(value).trim() !== ''
                ) // Filter out custom_int fields and empty values
                .map(([key, value]) => (
                  <input
                    key={key}
                    type="hidden"
                    name={key}
                    value={String(value)}
                  />
                ))}
              <button type="submit" className="payfast-button">
                Continue to PayFast
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayFastPayment;

